// Every string on the Guardrails page, so the wording still open in DP-20 §3 Q5 changes in
// one file. The intro and the When to price help come from the prototype
// (Guardrails-prototype-ss.png on DP-20).

export const PAGE_TITLE = 'Guardrails'
export const intro = (engineName: string) =>
  `Publish limits and when to price for ${engineName}. Floor, ceiling, and max step are per engine and editable. Booked players keep the rate they booked; open times take the new published price.`
export const NO_ENGINE = 'Select an engine in the header to see its guardrails.'
export const NO_ENGINES_HEADING = 'No dynamic engines yet'
export const NO_ENGINES_BODY = 'Add an engine to set its guardrails.'
export const GO_TO_ENGINES = 'Go to Engines'

export const LOADING_GUARDRAILS = 'Loading guardrails…'
export const LOAD_FAILED = 'The guardrails could not be loaded.'
export const RETRY = 'Retry'
export const READ_ONLY = 'You can view these guardrails but not change them.'

export const SAVING = 'Saving…'
export const SAVE_FAILED = 'The changes could not be saved. Try again.'
export const STALE =
  'These guardrails were changed by someone else since you opened the page. Reload to see them, then enter your changes again.'
export const RELOAD = 'Reload'

// The toast after a save, as in the prototype: the section's heading in bold, then this.
export const SAVED_TOAST = 'has been changed and saved'

// ── When to price ───────────────────────────────────────────────────────────

export const WINDOW_HEADING = 'When to price'
export const STOP_LABEL = 'Stop adjusting within'
export const STOP_HELP = 'How close to the tee time we stop changing published rates.'
export const HORIZON_LABEL = 'Price how far out'
export const HORIZON_SUFFIX = 'days'
export const HORIZON_HELP = 'Farthest tee time we will dynamically price (1–365 days).'
export const WINDOW_DEFAULT_NOTE =
  'Default is stop within 24 hours and price 60 days out. Stop adjusting must be closer than how far out we price. Hours inside the stop window still show a rec; they cannot be accepted or written. Past hour blocks show the published rate only.'
export const SAVE_WINDOW = 'Save window'

export const HORIZON_RANGE = 'Price how far out must be 1 to 365 days.'
export const HORIZON_WHOLE_DAYS = 'Price how far out must be whole days.'
export const STOP_NOT_CLOSER = 'The stop must be closer to tee time than how far out we price.'
export const STOP_CHOOSE = 'Choose when revUP stops adjusting.'

// ── Default limits ──────────────────────────────────────────────────────────

export const LIMITS_HEADING = 'Default limits'
// DP-20 §3 Q5: the prototype's text without the unfilled-calendar aside and the Autopilot
// sentence. In parts so "$ / %" can be bold without v-html (FE-SEC-01).
export const LIMITS_HELP = {
  lead: 'These limits apply when no seasonal timeframe matches that hour. Switch ',
  toggle: '$ / %',
  rest: ' in the Unit toggle: dollars reduce or increase from the engine’s price-class base, or percent of that base. The slider always shows floor and ceiling in dollars; max step follows the selected unit.',
} as const
export const PREVIEW_BASE = {
  lead: 'Price-class base for this preview: ',
  rest: ' (each tee time uses its own base at publish).',
} as const
export const NO_BASE_REASONS: Record<string, string> = {
  'not-yet-fetched':
    'the base price for this engine is still loading from foreUP; check again in a minute',
  'no-season-covers-today': 'no season covers today',
  'no-default-timeframe': 'the season has no Default timeframe',
  'no-price-set-for-18-holes': 'there is no 18-hole price',
}
export const noBaseText = (reason: string | null) =>
  `No price-class base was found for today${reason && NO_BASE_REASONS[reason] ? ` (${NO_BASE_REASONS[reason]})` : ''}. You can still save the limits.`
export const BASE_FAILED = 'Today’s base could not be loaded. You can still save the limits.'
export const LOADING_BASE = 'Loading today’s base…'

export const UNIT_LABEL = 'Unit'
export const UNIT_DOLLAR = '$'
export const UNIT_PERCENT = '%'
export const UNIT_DOLLAR_NAME = 'Dollars'
export const UNIT_PERCENT_NAME = 'Percent'
export const convertedNote = (unit: 'percent' | 'dollar', base: string) =>
  `Values converted to ${unit === 'dollar' ? 'dollars' : 'percent'} using today’s ${base} base. Save limits to keep them.`
export const CHECK_VALUES_NOTE =
  'There is no base for today, so the values were not converted. Check them before saving.'

export const FIELD_LABELS = {
  percent: { floor: 'Floor %', ceiling: 'Ceiling %', maxStep: 'Max step' },
  dollar: { floor: 'Reduce up to', ceiling: 'Increase up to', maxStep: 'Max step' },
} as const
// The unit beside a field, as in the prototype: max step in both units, and floor and ceiling
// in dollars. Empty means no text beside the field.
export const FIELD_SUFFIXES = {
  percent: { floor: '', ceiling: '', maxStep: '% vs published' },
  dollar: { floor: '$ below base', ceiling: '$ above base', maxStep: '$ vs published' },
} as const

export const SLIDER_LABEL = 'Floor and ceiling in dollars'
export const FLOOR = 'Floor'
export const CEILING = 'Ceiling'
export const BASE = 'Base'
export const SAVE_LIMITS = 'Save limits'

// The prototype's messages. A decimal or an empty field breaks the whole-number range, so it
// gets the same message.
export const FLOOR_PERCENT_RANGE = 'Floor must be 1 to 100% of base.'
export const CEILING_PERCENT_RANGE = 'Ceiling must be 100 to 300% of base.'
export const MAX_STEP_PERCENT_RANGE = 'Max step must be 1 to 50% per published change.'
// A dollar floor is an amount below base, up to $150 and never more than the base itself.
export const floorDollarRange = (max: number) => `Floor reduction must be $0–$${max} below base.`
// A dollar ceiling is an amount above base, up to $150 and never past the 300%-of-base top.
export const ceilingDollarRange = (max: number) => `Ceiling increase must be $0–$${max} above base.`
// A dollar max step runs from $1 to 50% of base, so its top changes with the base.
export const maxStepDollarRange = (max: number) =>
  `Max step must be $1–$${max} (50% of base) per published change.`
